
import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, IIdentityProvider} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ASync} from "../PA.Core/Basic/ASync";

import {EDLPAnalysisPeroidRow, EDLPAnalysisRow} from "./EDLPAnalysisBO.Rows"
import {IHaveExecutionBounds} from "../PA.Core/PA.Core.IF.Aspects";
import {ManualExecutionBounds} from "../PA.Utils/ManualExecutionBounds";
import {EDLPAnalysisCalculator} from "./ELDPAnalysisCalculator";
import {
    ICalculatedMetrics,
    IMetricCalculator,
    IMetricCalculatorCalculationChangedEvent,
    IPeriod,
    IPeriodMetricContext
} from "../PA.UIM.Metric/PA.UIM.Metric.IF";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {IMetricParameterSet, IMetrics} from "../PA.Metric.Services/PA.Metrics.IF";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {FinancialUtils} from "../PA.Core/Basic/FinancialUtils";
import {IHaveConditions} from "../PA.MD.Core/PA.MD.IF.Aspects";
import {ConditionCollection} from "../PA.Utils/ConditionCollection";
import {WB_CustomerRow, WB_FinishedProductRow, WB_ProductGroupRow} from "../PA.Core/PAdbSchemaRows";
import {PlanningDataManualExecutionBoundsProductsProvider} from "../PA.Utils/ManualExecutionBoundsUtils";
import {PAEnvironment} from "../PA.Core/PAEnvironment";

export interface IEDLPAnalysisCreateOptions {
    dbContext?: PAdbContext,
    ExecutionBoundsAll?: IHaveExecutionBounds
}



export class EDLPAnalysisBO
    implements IBusinessObject,
        IMetricCalculator
{
    Identity: IdentityProvider;

    CreateOptions: IEDLPAnalysisCreateOptions;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;

    drEDLPAnalysis: EDLPAnalysisRow
    private _ExecutionBoundsAll: IHaveExecutionBounds
    private _ExecutionBoundsSelected: ManualExecutionBounds

    private _EDLPCalculator: EDLPAnalysisCalculator

    private _ConditionContainer: IHaveConditions

    constructor(options?: IEDLPAnalysisCreateOptions) {
        this.CreateOptions = options || {};

        if('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        } else {
            this.dbContext = new PAdbContext()
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New EDLP Analysis'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        let pdPP = new PlanningDataManualExecutionBoundsProductsProvider()
        this._ExecutionBoundsSelected = new ManualExecutionBounds({dbContext: this.dbContext, bPopulateWithPlanningDataBounds: false, ProductsProvider: pdPP})
        if('ExecutionBoundsAll' in this.CreateOptions){
            this._ExecutionBoundsAll = this.CreateOptions.ExecutionBoundsAll
        } else {
            this._ExecutionBoundsAll = new ManualExecutionBounds({dbContext: this.dbContext, bPopulateWithPlanningDataBounds: true, ProductsProvider: pdPP})
        }

        // @ts-ignore
        this._ConditionContainer = new ConditionCollection({dbContext: this.dbContext})
        let self = this
        this._ExecutionBoundsSelected.ExecutionBoundsChanged.subscribe(function() {
            let arrCustomers = _.map(self._ExecutionBoundsSelected.Execution_PlanningCustomers, (aCust: WB_CustomerRow) => { return aCust.cif.CUSTOMER_ID })
            if(arrCustomers.length == 0)
                arrCustomers = undefined
            let arrPrds = _.map(self._ExecutionBoundsSelected.Execution_ProductGroups, (aPrd: WB_ProductGroupRow) => { return aPrd.cif.PRODUCT_GROUP_ID })
            if(arrPrds.length == 0)
                arrPrds = undefined

            if(arrCustomers && arrPrds) {
                self._ConditionContainer.LoadConditions(undefined, arrCustomers, arrPrds, self._ExecutionBoundsSelected.Execution_InStoreStartDt, self._ExecutionBoundsSelected.Execution_InStoreEndDt)
                self._ConditionContainer.Customers = self._ExecutionBoundsSelected.Execution_PlanningCustomers
                self._ConditionContainer.Products = self._ExecutionBoundsSelected.Execution_ProductGroups
            }
        })

        this._EDLPCalculator = new EDLPAnalysisCalculator()

        this.drEDLPAnalysis = new EDLPAnalysisRow()
        this.drEDLPAnalysis.Elasticity = -2

        this.IMetricCalculator_init()

    }

    //<editor-fold desc = "EDLP Analysis Properties">

    get ExecutionBoundsAll(): IHaveExecutionBounds {
        return this._ExecutionBoundsAll
    }

    get ConditionContainer(): IHaveConditions {
        return this._ConditionContainer
    }

    get ExecutionBoundsSelected(): ManualExecutionBounds {
        return this._ExecutionBoundsSelected
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        self.LoadProgressProvider.start("Creating Job");
        self._OnLoadComplete = new Promise<IBusinessObject>( (resolve, error) => {

            self.LoadOptions = options || {};
            if('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null) {
                const sError = "Unable to determine dbContext for new EDLP Analysis.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }


            self.ExecutionBoundsAll.OnIHaveExecutionBoundsLoaded
                .then(() => {
                    self.ExecutionBoundsSelected.pauseChangeNotifications()
                    self.ExecutionBoundsSelected.Execution_InStore =  self.ExecutionBoundsAll.Execution_InStore
                    self.ExecutionBoundsSelected.Execution_InStore_Aligned = self.ExecutionBoundsAll.Execution_InStore_Aligned
                    self.ExecutionBoundsSelected.Execution_Shipment =  self.ExecutionBoundsAll.Execution_Shipment
                    self.ExecutionBoundsSelected.Execution_Shipment_Aligned = self.ExecutionBoundsAll.Execution_Shipment_Aligned
                    self.ExecutionBoundsSelected.resumeChangeNotifications()
                    self._bIsLoaded = true
                    self._EDLPCalculator.register(self)
                    resolve(self)
                }).catch((err) => {
                error(err)
            })

        });

        return this._OnLoadComplete;
    }


    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        console.log("Load EDLP Analysis Not Supported")
        return undefined;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {

        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.Identity.name + "(" + self.Identity.id + ")");
        return new Promise<IBusinessObject>( (resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if(self.dbContext == null) {
                var sError = "Unable to determine dbContext for to Validate EDLP Analysis.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            self._bIsValidated = true;
            self.ValidateProgressProvider.finish();
            resolve(self);
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean,  options?: any): Promise<IBusinessObject> {

        var self = this;

        this.SaveProgressProvider.start("Saving " + self.Identity.name + "(" + self.Identity.id_ui + ")");

        return new Promise<IBusinessObject>( (resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if(this.dbContext == null) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save EDLP Analysis.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }
            self.SaveOptions = options || {};

            var saveFunction: (esName: string, saveRecords: ESRecord[]| ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }


            self._bIsSaved = true;
            self.SaveProgressProvider.finish();
            resolve(self);
        });
    }

    get  bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        return ASync.error("Delete not supported by ELDP Analysis")
    }



    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc = "IMetricsCalculator operations">

    private _CalculationChanged: BehaviorSubject<IMetricCalculatorCalculationChangedEvent>;

    IMetricCalculator_init(){
        const self = this

        self._CalculationChanged = new BehaviorSubject<IMetricCalculatorCalculationChangedEvent>(undefined)


        self.drEDLPAnalysis.setupOnChangeEvents(['Elasticity'], self.onMainRowChange, {EDLPAnalysisBO: self})

        let changeFields = ['CnsStartDt','CnsEndDt','TrueBasePriceOvrd']

        self.drEDLPAnalysis.PriceChange.setupOnChangeEvents(changeFields, self.onPeriodRowChanged, {EDLPAnalysisBO: self})
        self.drEDLPAnalysis.Baseline.setupOnChangeEvents(changeFields, self.onPeriodRowChanged, {EDLPAnalysisBO: self})

    }

    get CalculationChanged(): BehaviorSubject<IMetricCalculatorCalculationChangedEvent> {
        return this._CalculationChanged
    }

    BeforeCalcualteMetrics(pmc: IPeriodMetricContext,mps: IMetricParameterSet): Promise<void> {
        return ASync.resolve();
    }

    CalculateMetrics(pmc: IPeriodMetricContext, aPeriod: IPeriod, inMetrics: IMetrics): Promise<ICalculatedMetrics> {
        const self = this

        return new Promise<ICalculatedMetrics>((resolve, error) => {
            let returnMetrics = {
                TRUE_BASE_PRICE: 0,
                OBSERVED_BASE_PRICE: inMetrics.RETAILER_BASE_PRICE,
                BASE_DISCOUNT_PCNT: 0,
                EDLP_LIFT_PCNT: 0,

                CNSMP_BASE_UNITS:      inMetrics.CNSMP_SYND_BASE_UNITS,
                CNSMP_BASE_RTLSLS:     inMetrics.CNSMP_SYND_BASE_RTLSLS ,
                CNSMP_BASE_STAT:       inMetrics.CNSMP_SYND_BASE_STAT ,
                CNSMP_BASE_WT:         inMetrics.CNSMP_SYND_BASE_WT ,
                CNSMP_BASE_LISTSLS:    inMetrics.CNSMP_SYND_BASE_LISTSLS ,
                CNSMP_BASE_NETLISTSLS: inMetrics.CNSMP_SYND_BASE_NETLISTSLS ,
                CNSMP_BASE_PRFTCNTRB:  inMetrics.CNSMP_SYND_BASE_PRFTCNTRB,

                CNSMP_EDLP_UNITS: 0,
                CNSMP_EDLP_RTLSLS: 0,
                CNSMP_EDLP_STAT: 0,
                CNSMP_EDLP_WT: 0,
                CNSMP_EDLP_LISTSLS: 0,
                CNSMP_EDLP_NETLISTSLS: 0,
                CNSMP_EDLP_PRFTCNTRB: 0,

                CNSMP_SYND_BASE_UNITS:      inMetrics.CNSMP_SYND_BASE_UNITS ,
                CNSMP_SYND_BASE_RTLSLS:     inMetrics.CNSMP_SYND_BASE_RTLSLS,
                CNSMP_SYND_BASE_STAT:       inMetrics.CNSMP_SYND_BASE_STAT,
                CNSMP_SYND_BASE_WT:         inMetrics.CNSMP_SYND_BASE_WT,
                CNSMP_SYND_BASE_LISTSLS:    inMetrics.CNSMP_SYND_BASE_LISTSLS,
                CNSMP_SYND_BASE_NETLISTSLS: inMetrics.CNSMP_SYND_BASE_NETLISTSLS,
                CNSMP_SYND_BASE_PRFTCNTRB:  inMetrics.CNSMP_SYND_BASE_PRFTCNTRB

            }

            let liftInputs = self.findTrueBasePriceElasticity(aPeriod)
            if(_.isEmpty(liftInputs)){
                // do nothing, defaulted above
            } else {
                // Split EDLP Volume
                returnMetrics.TRUE_BASE_PRICE = liftInputs.TrueBasePrice
                returnMetrics.BASE_DISCOUNT_PCNT = -1 * FinancialUtils.calculateDiscountPcnt(liftInputs.TrueBasePrice, returnMetrics.OBSERVED_BASE_PRICE)
                returnMetrics.EDLP_LIFT_PCNT = FinancialUtils.calculateEDLPLift(liftInputs.Elasticity, liftInputs.TrueBasePrice, returnMetrics.OBSERVED_BASE_PRICE)

                returnMetrics.CNSMP_EDLP_UNITS = FinancialUtils.calculateIncremental(returnMetrics.CNSMP_SYND_BASE_UNITS, returnMetrics.EDLP_LIFT_PCNT)
                let pcntUnits = 0
                if(returnMetrics.CNSMP_SYND_BASE_UNITS > 0){
                    pcntUnits = returnMetrics.CNSMP_EDLP_UNITS / returnMetrics.CNSMP_SYND_BASE_UNITS
                }

                returnMetrics.CNSMP_EDLP_RTLSLS = pcntUnits * returnMetrics.CNSMP_SYND_BASE_RTLSLS
                returnMetrics.CNSMP_EDLP_STAT = pcntUnits * returnMetrics.CNSMP_SYND_BASE_STAT
                returnMetrics.CNSMP_EDLP_WT = pcntUnits * returnMetrics.CNSMP_SYND_BASE_WT
                returnMetrics.CNSMP_EDLP_LISTSLS = pcntUnits * returnMetrics.CNSMP_SYND_BASE_LISTSLS
                returnMetrics.CNSMP_EDLP_NETLISTSLS = pcntUnits * returnMetrics.CNSMP_SYND_BASE_NETLISTSLS
                returnMetrics.CNSMP_EDLP_PRFTCNTRB = pcntUnits * returnMetrics.CNSMP_SYND_BASE_PRFTCNTRB

                returnMetrics.CNSMP_BASE_UNITS      = returnMetrics.CNSMP_SYND_BASE_UNITS      - returnMetrics.CNSMP_EDLP_UNITS
                returnMetrics.CNSMP_BASE_RTLSLS     = returnMetrics.CNSMP_SYND_BASE_RTLSLS     - returnMetrics.CNSMP_EDLP_RTLSLS
                returnMetrics.CNSMP_BASE_STAT       = returnMetrics.CNSMP_SYND_BASE_STAT       - returnMetrics.CNSMP_EDLP_STAT
                returnMetrics.CNSMP_BASE_WT         = returnMetrics.CNSMP_SYND_BASE_WT         - returnMetrics.CNSMP_EDLP_WT
                returnMetrics.CNSMP_BASE_LISTSLS    = returnMetrics.CNSMP_SYND_BASE_LISTSLS    - returnMetrics.CNSMP_EDLP_LISTSLS
                returnMetrics.CNSMP_BASE_NETLISTSLS = returnMetrics.CNSMP_SYND_BASE_NETLISTSLS - returnMetrics.CNSMP_EDLP_NETLISTSLS
                returnMetrics.CNSMP_BASE_PRFTCNTRB  = returnMetrics.CNSMP_SYND_BASE_PRFTCNTRB  - returnMetrics.CNSMP_EDLP_PRFTCNTRB

            }

            resolve(returnMetrics)
        })
    }


    private findTrueBasePriceElasticity(aPeriod: IPeriod): {Elasticity: number, TrueBasePrice: number } {

        let returnValue: {Elasticity: number, TrueBasePrice: number } = undefined

        _.forEach([this.drEDLPAnalysis.Baseline, this.drEDLPAnalysis.PriceChange], (aEAPRow) => {
            if (DateUtils.RangeDatesOverlap(aEAPRow.CnsStartDt, aEAPRow.CnsEndDt,
                aPeriod.Range.StartDt, aPeriod.Range.EndDt)
                && (aEAPRow.TrueBasePriceOvrd > 0)) {
                returnValue = {
                    Elasticity: this.drEDLPAnalysis.Elasticity,
                    TrueBasePrice: aEAPRow.TrueBasePriceOvrd
                }
                return false // stop iterating
            }
        })

        return returnValue
    }

    private onMainRowChange(record: EDLPAnalysisRow, propertyName: string, newValue: any, opts: {EDLPAnalysisBO: EDLPAnalysisBO}) {
        //
        console.log("Metric Calc: Changed field: ", propertyName, " value: ", newValue)
        opts.EDLPAnalysisBO.notifyMetricCalculationChanged()
    }

    private onPeriodRowChanged(record: EDLPAnalysisPeroidRow, propertyName: string, newValue: any, opts: {EDLPAnalysisBO: EDLPAnalysisBO}) {
        //
        console.log("Metric Calc: Changed field: ", propertyName, " value: ", newValue)
        opts.EDLPAnalysisBO.notifyMetricCalculationChanged()
    }


    private notifyMetricCalculationChanged(){
        this.CalculationChanged.next({MetricCalculator: this})
    }



    //</editor-fold>

};

PAEnvironment.registerBusinessObjectClass( EDLPAnalysisBO , "EDLPAnalysisBO", "EDLP Analysis")

